import { CustomersService } from './../customers/customers.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ConfigService } from '../config';
import {
  ADBANNERS_HIDDEN_KEY,
  SURVEY_SHOWN_KEY_PREFIX,
  TOKEN_KEY,
  USER_KEY,
} from '../../types/constants';
import { Router } from '@angular/router';
import { BackendResponse, JwtPayload } from '../../types/interfaces';
import { jwtDecode } from 'jwt-decode';
import { StorageUser, User } from '../../../shared/models/user.model';
import { replaceUrlParams } from '../utils/helpers';
import { endOfDayDate } from '../../../shared/utils/endOfDayDate';
import { TestCustomerService } from '../test-customer/test-customer.service';

interface LoginInfo {
  user: User;
  token: string;
  forcePasswordChange: boolean;
  specialUser: boolean;
  firstLogin: boolean;
}

interface LoginWithProviderBody {
  provider: string;
  user: string;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private router: Router,
    private testCustomerService: TestCustomerService,
    private customersService: CustomersService
  ) {}

  login(
    username: string,
    password: string,
    redirectTo: string = null
  ): Observable<BackendResponse<LoginInfo>> {
    const url = this.configService.endpoints.auth.login;
    const credentials = { username, password };

    const request = this.http
      .post<BackendResponse<LoginInfo>>(url, credentials)
      .pipe(
        tap(response => {
          if (response && (response.data as LoginInfo).token) {
            const storageUser = new StorageUser(response.data.user);
            this.setToken((response.data as LoginInfo).token);
            localStorage.setItem(USER_KEY, JSON.stringify(storageUser));
            localStorage.setItem(ADBANNERS_HIDDEN_KEY, 'false');
            if (redirectTo) {
              window.open(redirectTo, '_self');
              return;
            }
            // Nothing to choose with a single customer - go straight to the
            // shop. Multi-customer users pick one first, and the onboarding
            // guard decides which select screen they land on.
            if (response.data.user.customerIds.length === 1) {
              this.customersService
                .setCustomer(response.data.user.customerIds[0])
                .subscribe({
                  next: (res: any) => {
                    this.setToken(res.data.token);
                    this.router.navigate(['/shop'], {
                      queryParams: {
                        customerId: response.data.user.customerIds[0],
                      },
                    });
                  },
                });
            } else {
              this.router.navigate(['/customer-select']);
            }
          }
        })
      );

    return request;
  }

  loginWithProvider(
    data: LoginWithProviderBody
  ): Observable<BackendResponse<LoginInfo>> {
    const url = this.configService.endpoints.auth.loginWithProvider;
    return this.http.post<BackendResponse<LoginInfo>>(url, data).pipe(
      tap(response => {
        if (response && (response.data as LoginInfo).token) {
          const storageUser = new StorageUser(response.data.user);
          this.setToken((response.data as LoginInfo).token);
          localStorage.setItem(TOKEN_KEY, (response.data as LoginInfo).token);
          localStorage.setItem(USER_KEY, JSON.stringify(storageUser));
          localStorage.setItem(ADBANNERS_HIDDEN_KEY, 'false');
          // Single customer means nothing to choose - straight to the shop.
          if (response.data.user.customerIds.length === 1) {
            this.customersService
              .setCustomer(response.data.user.customerIds[0])
              .subscribe({
                next: (res: any) => {
                  this.setToken(res.data.token);
                  this.router.navigate(['/shop'], {
                    queryParams: {
                      customerId: response.data.user.customerIds[0],
                    },
                  });
                },
              });
          } else {
            this.router.navigate(['/customer-select']);
          }
        }
      })
    );
  }

  forgotPassword(login: string): Observable<BackendResponse> {
    const url = this.configService.endpoints.auth.forgotPassword;
    return this.http.post<BackendResponse>(url, { login });
  }

  saveLoginDate(): Observable<BackendResponse> {
    const url = this.configService.endpoints.auth.saveLoginDate;
    return this.http.get<BackendResponse>(url);
  }

  resetPassword(
    token: string,
    login: string,
    password: string
  ): Observable<BackendResponse> {
    const url = this.configService.endpoints.auth.resetPassword;
    return this.http.post<BackendResponse>(url, { password, token, login });
  }

  changePassword(
    username: string,
    oldPassword: string,
    newPassword: string
  ): Observable<BackendResponse> {
    const url = this.configService.endpoints.auth.changePassword;
    return this.http.post<BackendResponse>(url, {
      username,
      oldPassword,
      newPassword,
    });
  }

  validateResetPassword(
    token: string,
    login: string
  ): Observable<BackendResponse<{ token: string; login: string }>> {
    const url = replaceUrlParams(
      this.configService.endpoints.auth.validateResetPassword,
      ['token', 'login'],
      [token, login]
    );
    return this.http
      .get<BackendResponse<{ token: string; login: string }>>(url)
      .pipe(
        catchError(error => {
          this.router.navigate(['/login/forgot-password'], {
            queryParams: { invalidResetToken: true },
          });
          throw error;
        })
      );
  }

  logout(): void {
    document.cookie =
      'auth_token=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.bringliesel.de';
    const customerId = this.getCustomerIdFromToken();
    if (customerId) {
      localStorage.removeItem(`${SURVEY_SHOWN_KEY_PREFIX}${customerId}`);
    }
    localStorage.removeItem(TOKEN_KEY);
    this.testCustomerService.clear();
    this.router.navigate(['/login']);
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  }

  setToken(token: string | null): void {
    if (!token) return;
    localStorage.setItem(TOKEN_KEY, token);
    // Must be path=/ so the single token cookie is refreshed on every rotation
    // and is sent to /api requests (e.g. the SEPA PDF endpoint). Without it the
    // cookie inherits the current route's directory (e.g. /residents) and the
    // stale root-scoped cookie eventually expires, redirecting the SEPA form to
    // customer-select-transition.
    document.cookie = `token=${token};path=/`;
    document.cookie = `auth_token=hilfe-enabled;expires=${endOfDayDate().toString()};path=/;domain=.bringliesel.de`;
  }

  getLoggedUserFromStorage(): StorageUser {
    const user = localStorage.getItem(USER_KEY);
    return user ? new StorageUser(JSON.parse(user)) : null;
  }

  getToken(): string {
    return localStorage.getItem(TOKEN_KEY) || '';
  }

  getLastLogin(): string {
    const data = this.decodeToken();
    return data?.last_login || '';
  }

  getCustomerIdFromToken(): number {
    if (this.getToken()) {
      return this.decodeToken()?.customer_id;
    } else {
      return undefined;
    }
  }

  loggedInAsTestCustomer(): boolean {
    if (this.getToken()) {
      return this.decodeToken().testCustomer;
    } else {
      return false;
    }
  }

  /**
   * New users get the onboarding customer-select screen for their first five
   * distinct login days; after that they graduate to the plain one.
   */
  shouldShowOnboarding() {
    const loggedUser = this.getLoggedUserFromStorage();
    return (
      !loggedUser.newShopLoginDates || loggedUser.newShopLoginDates.length < 5
    );
  }

  decodeToken() {
    const decodedToken = localStorage.getItem(TOKEN_KEY);
    if (!decodedToken) return;
    try {
      return jwtDecode<JwtPayload>(decodedToken);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }
}
